import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Navbar from './components/Navbar';
import LandingPage from './pages/LandingPage';
import AuthPage from './pages/AuthPage';
import AuthCallback from './pages/AuthCallback';
import SubscriptionPage from './pages/SubscriptionPage';
import ContentGallery from './pages/ContentGallery';
import ChatPage from './pages/ChatPage';
import CreatorDashboard from './pages/CreatorDashboard';
import WishlistPage from './pages/WishlistPage';
import PaymentSuccess from './pages/PaymentSuccess';
import DMCAPage from './pages/DMCAPage';
import AIEditorPage from './pages/AIEditorPage';
import ScanDashboard from './pages/ScanDashboard';
import DevKitPage from './pages/DevKitPage';
import VaultPage from './pages/VaultPage';
import EnforcementDashboard from './pages/EnforcementDashboard';
import VIGProtocolPage from './pages/VIGProtocolPage';
import MarketplacePage from './pages/MarketplacePage';
import TreasuryPage from './pages/TreasuryPage';
import GamificationPage from './pages/GamificationPage';
import StreamingDashboard from './pages/StreamingDashboard';
import PreviewDocPage from './pages/PreviewDocPage';
import ShopPage from './pages/ShopPage';
import AssetManagerPage from './pages/AssetManagerPage';
import SUDSConsolePage from './pages/SUDSConsolePage';
import BrandLandingPage from './pages/BrandLandingPage';
import QSLMErrorBoundary from './components/QSLMErrorBoundary';
import SmithersOrb from './components/SmithersOrb';
import axios from 'axios';
import './index.css';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function SeedOnMount() {
  useEffect(() => {
    axios.post(`${API}/seed`).catch(() => {});
    axios.post(`${API}/enforcement/seed`).catch(() => {});
    axios.post(`${API}/marketplace/seed`).catch(() => {});
  }, []);
  return null;
}

function AppRoutes() {
  const location = useLocation();

  // CRITICAL: Detect session_id in URL hash synchronously during render
  // This prevents race conditions with ProtectedRoute checks
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }

  const showNavbar = !['/auth', '/'].includes(location.pathname) && !location.pathname.startsWith('/launch/');
  const showOrb = !location.pathname.startsWith('/launch/');

  return (
    <>
      <ScrollToTop />
      <SeedOnMount />
      {showNavbar && <Navbar />}
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/subscribe" element={<SubscriptionPage />} />
        <Route path="/gallery" element={<ContentGallery />} />
        <Route path="/chat" element={<ChatPage />} />
        <Route path="/dashboard" element={<CreatorDashboard />} />
        <Route path="/wishlist" element={<WishlistPage />} />
        <Route path="/editor" element={<AIEditorPage />} />
        <Route path="/scan" element={<ScanDashboard />} />
        <Route path="/devkit" element={<DevKitPage />} />
        <Route path="/vault" element={<VaultPage />} />
        <Route path="/enforcement" element={<EnforcementDashboard />} />
        <Route path="/vig" element={<VIGProtocolPage />} />
        <Route path="/marketplace" element={<MarketplacePage />} />
        <Route path="/treasury" element={<TreasuryPage />} />
        <Route path="/achievements" element={<GamificationPage />} />
        <Route path="/streaming" element={<StreamingDashboard />} />
        <Route path="/reports" element={<PreviewDocPage />} />
        <Route path="/shop" element={<ShopPage />} />
        <Route path="/assets" element={<AssetManagerPage />} />
        <Route path="/suds" element={<QSLMErrorBoundary><SUDSConsolePage /></QSLMErrorBoundary>} />
        <Route path="/launch/:brand" element={<BrandLandingPage />} />
        <Route path="/payment-success" element={<PaymentSuccess />} />
        <Route path="/dmca" element={<DMCAPage />} />
      </Routes>
      {showOrb && <SmithersOrb />}
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
